'use client';

/**
 * Discover 事件流列表。
 *
 * GET /api/market/event-streaming/events?order_by=latest&limit=6&offset=0
 * 分页 limit/offset（limit 1-100），支持 query 搜索、event_type 过滤、view 精简。
 * 响应 data: { feed, limit, offset, order_by, has_more, pagination }。
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useSWRInfinite, { type SWRInfiniteConfiguration } from 'swr/infinite';
import customFetch from '@/lib/utils/fetch';
import type { MarketEventFeedItem } from '../ui/MarketEvent';

export type EventsStreamOrderBy =
  | 'trending'
  | 'hot_events'
  | 'latest'
  | 'unsorted'
  | 'market_volume_24h';

export type EventsStreamEventType =
  | 'politics'
  | 'geopolitics'
  | 'macro'
  | 'crypto'
  | 'earnings'
  | 'finance'
  | 'legal'
  | 'soccer'
  | 'sports'
  | 'other';

export type EventsStreamView = 'full' | 'compact';

export interface EventsStreamData {
  feed: MarketEventFeedItem[];
  limit: number;
  offset: number;
  order_by: EventsStreamOrderBy;
  type: 'event';
  view?: EventsStreamView;
  query?: string | null;
  has_more?: boolean;
}

export interface EventsStreamResponse {
  success: boolean;
  data: EventsStreamData;
  message?: string;
  error?: string;
}

export interface FetchEventsStreamParams {
  orderBy?: EventsStreamOrderBy;
  eventType?: EventsStreamEventType | null;
  query?: string | null;
  view?: EventsStreamView;
  updatedWithinHours?: number | null;
  limit?: number;
  offset?: number;
}

export interface UseEventsStreamOptions extends Omit<FetchEventsStreamParams, 'limit' | 'offset'> {
  pageSize?: number;
  enabled?: boolean;
  swr?: SWRInfiniteConfiguration<EventsStreamResponse, Error>;
}

const DEFAULT_PAGE_SIZE = 6;
const DEFAULT_ORDER_BY: EventsStreamOrderBy = 'latest';

export async function fetchEventsStream(params: FetchEventsStreamParams = {}) {
  const {
    orderBy = DEFAULT_ORDER_BY,
    eventType,
    query,
    view,
    updatedWithinHours,
    limit = DEFAULT_PAGE_SIZE,
    offset = 0,
  } = params;

  const searchParams = new URLSearchParams({
    order_by: orderBy,
    limit: String(limit),
    offset: String(offset),
  });
  if (eventType) searchParams.set('event_type', eventType);
  if (query) searchParams.set('query', query);
  if (view) searchParams.set('view', view);
  if (updatedWithinHours != null) {
    searchParams.set('updated_within_hours', String(updatedWithinHours));
  }

  const response = await customFetch(
    `/api/market/event-streaming/events?${searchParams.toString()}`,
  );

  if (!(response instanceof Response)) {
    throw response instanceof Error ? response : new Error('Failed to fetch events stream');
  }

  const result = (await response.json()) as EventsStreamResponse;

  if (!result.success) {
    throw new Error(result.message || result.error || 'Failed to fetch events stream');
  }

  return result;
}

export function useEventsStream(options: UseEventsStreamOptions = {}) {
  const {
    orderBy = DEFAULT_ORDER_BY,
    eventType = null,
    query = null,
    view,
    updatedWithinHours = null,
    pageSize = DEFAULT_PAGE_SIZE,
    enabled = true,
    swr: swrOptions,
  } = options;

  // 搜索词交给 key，变化时 SWR 自动从第一页重新拉取
  const normalizedQuery = query?.trim() || null;

  const getKey = useCallback(
    (pageIndex: number, previousPageData: EventsStreamResponse | null) => {
      if (!enabled) return null;
      // 上一页不足一页说明到底了，返回 null 停止翻页
      if (previousPageData && previousPageData.data.feed.length < pageSize) return null;

      return [
        'events-stream',
        pageSize,
        pageIndex,
        orderBy,
        eventType,
        normalizedQuery,
        view ?? null,
        updatedWithinHours,
      ] as const;
    },
    [enabled, pageSize, orderBy, eventType, normalizedQuery, view, updatedWithinHours],
  );

  const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite<
    EventsStreamResponse,
    Error
  >(
    getKey,
    ([, limit, pageIndex]: readonly [string, number, number, ...unknown[]]) =>
      fetchEventsStream({
        orderBy,
        eventType,
        query: normalizedQuery,
        view,
        updatedWithinHours,
        limit,
        offset: pageIndex * limit,
      }),
    {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
      ...swrOptions,
    },
  );

  // memo 保持引用稳定：data 未变化时 list 不产生新数组，避免下游 useEffect/useMemo 反复触发
  const list = useMemo(() => data?.flatMap((page) => page?.data?.feed ?? []) ?? [], [data]);
  const lastPage = data?.[data.length - 1];
  // 接口返回了 has_more 就用它，否则退回“最后一页是否满员”
  const hasMore = lastPage
    ? (lastPage.data.has_more ?? lastPage.data.feed.length >= pageSize)
    : false;

  const loadMore = useCallback(() => {
    if (!isValidating && hasMore) {
      setSize((s) => s + 1);
    }
  }, [isValidating, hasMore, setSize]);

  // IntersectionObserver sentinel — 绑定到列表底部元素，进入视口自动加载下一页。
  // 依赖 loadMore：加载完成后重建 observer，sentinel 若仍在视口内会再次触发，
  // 避免新一页填不满屏幕时自动加载停住
  const sentinelRef = useRef<Element | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const setSentinelRef = useCallback(
    (el: Element | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      sentinelRef.current = el;

      if (!el) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) loadMore();
        },
        { threshold: 0.1 },
      );
      observerRef.current.observe(el);
    },
    [loadMore],
  );

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return {
    list,
    hasMore,
    isLoading,
    isValidating,
    error,
    page: size,
    loadMore,
    setSentinelRef,
    refresh: mutate,
  };
}

export default useEventsStream;
